import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Container,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Chip
} from '@mui/material'
import {
  Visibility,
  VisibilityOff,
  Lock,
  Person,
  Security,
  CardMembership,
  WebAsset
} from '@mui/icons-material'
import { certificateApi } from '../utils/api'
import api from '../utils/api'

const Login = () => {
  const navigate = useNavigate()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [certCount, setCertCount] = useState(null)
  const [productsCount, setProductsCount] = useState(null)
  const [loadingCount, setLoadingCount] = useState(true)

  // Landing-page renewal tables
  const [renewalFilter, setRenewalFilter] = useState('upcoming') // 'upcoming' | 'renewed'
  const [recentlyRenewed, setRecentlyRenewed] = useState([])
  const [upcomingRenewals, setUpcomingRenewals] = useState([])
  const [loadingRenewals, setLoadingRenewals] = useState(true)

  // Fetch certificate and product counts on component mount
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.get('/auth/certificate-stats')
        if (response.data) {
          if (response.data.total_certificates !== undefined) {
            setCertCount(response.data.total_certificates)
          }
          if (response.data.total_products !== undefined) {
            setProductsCount(response.data.total_products)
          }
        }
      } catch (err) {
        console.log('Could not fetch stats:', err)
        setCertCount('N/A')
        setProductsCount('N/A')
      } finally {
        setLoadingCount(false)
      }
    }

    fetchStats()
  }, [])

  // Fetch renewal activity (recently renewed + upcoming) on mount
  useEffect(() => {
    const fetchRenewals = async () => {
      try {
        const response = await api.get('/auth/certificate-renewals')
        if (response.data) {
          setRecentlyRenewed(response.data.recently_renewed || [])
          setUpcomingRenewals(response.data.upcoming_renewals || [])
        }
      } catch (err) {
        console.log('Could not fetch renewals:', err)
      } finally {
        setLoadingRenewals(false)
      }
    }

    fetchRenewals()
  }, [])

  const formatDate = (value) => {
    if (!value) return '—'
    const d = new Date(value)
    return isNaN(d) ? '—' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
  }

  const renewalRows = renewalFilter === 'renewed' ? recentlyRenewed : upcomingRenewals

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      // Call your LDAP/Windows authentication API endpoint
      const response = await api.post('/auth/login', {
        username,
        password
      })

      // Tokens are now in httpOnly cookies (set by backend)
      // Just store username for display purposes
      if (response.data.message) {
        localStorage.setItem('username', username)
        localStorage.setItem('mail', response.data.mail)
        
        // Small delay to ensure cookies are fully set before navigation
        // This prevents race condition where components try to fetch data
        // before browser has processed Set-Cookie headers
        setTimeout(() => {
          navigate('/')
        }, 100)
      } else {
        setError('Authentication failed. Please try again.')
      }
    } catch (err) {
      console.error('Login error:', err)
      if (err.response?.status === 401) {
        setError('Invalid username or password.')
      } else if (err.response?.status === 500) {
        setError('Server error. Please contact support.')
      } else {
        setError('Unable to connect to authentication server.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword)
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        background: '#f5f7fa'
      }}
    >
      {/* Left Side - Hero Section */}
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #004681 0%, #2f79bf 100%)',
          padding: 4,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Decorative circles */}
        <Box
          sx={{
            position: 'absolute',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            top: -100,
            right: -100
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.05)',
            bottom: -50,
            left: -50
          }}
        />

        {/* Content */}
        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: '1000px', width: '100%', textAlign: 'center' }}>
          <Typography
            variant="h1"
            sx={{
              color: 'white',
              fontWeight: 700,
              fontSize: { md: '1.5rem', lg: '1.85rem' },
              mb: 1,
              lineHeight: 1,
              letterSpacing: '-0.02em',
              fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
              textTransform: 'uppercase',
              background: 'linear-gradient(135deg, #ffffff 0%, rgba(255, 255, 255, 0.85) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textShadow: '0 2px 20px rgba(255, 255, 255, 0.3)'
            }}
          >
            Cert Manage
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255, 255, 255, 0.95)',
              fontSize: '0.9rem',
              mb: 2,
              fontWeight: 400,
              lineHeight: 1.4
            }}
          >
            Secure, automated certificate lifecycle management for enterprise infrastructure
          </Typography>

          {/* Stats Display */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'stretch',
              justifyContent: 'center',
              gap: 2,
              mt: 1
            }}
          >
            {/* Certificates Managed */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                p: 1.5,
                borderRadius: 2,
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
            >
              <CardMembership sx={{ fontSize: 28, color: 'rgba(255, 255, 255, 0.4)' }} />
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.75rem' }}>
                  Certificates Managed
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: 'white', lineHeight: 1.1 }}>
                  {loadingCount ? (
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                  ) : (
                    certCount?.toLocaleString() || '0'
                  )}
                </Typography>
              </Box>
            </Box>

            {/* Products Onboarded */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                p: 1.5,
                borderRadius: 2,
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
            >
              <WebAsset sx={{ fontSize: 28, color: 'rgba(255, 255, 255, 0.4)' }} />
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.75rem' }}>
                  Products Onboarded
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: 'white', lineHeight: 1.1 }}>
                  {loadingCount ? (
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                  ) : (
                    productsCount?.toLocaleString() || '0'
                  )}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Renewal Activity Table */}
          <Box
            sx={{
              mt: 2.5,
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.97)',
              boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)',
              overflow: 'hidden',
              textAlign: 'left'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { md: 'column', lg: 'row' },
                alignItems: { md: 'stretch', lg: 'center' },
                justifyContent: 'space-between',
                gap: 1.5,
                p: 2,
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#004681' }}>
                Certificate Renewals
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={renewalFilter}
                onChange={(e, val) => val && setRenewalFilter(val)}
                sx={{
                  '& .MuiToggleButton-root': {
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    px: 1.5,
                    py: 0.5,
                    color: '#004681',
                    borderColor: 'rgba(0, 70, 129, 0.3)'
                  },
                  '& .Mui-selected': {
                    background: 'linear-gradient(135deg, #004681 0%, #2f79bf 100%) !important',
                    color: 'white !important'
                  }
                }}
              >
                <ToggleButton value="upcoming">
                  Upcoming (3 months) {upcomingRenewals.length ? `· ${upcomingRenewals.length}` : ''}
                </ToggleButton>
                <ToggleButton value="renewed">
                  Renewed (30 days) {recentlyRenewed.length ? `· ${recentlyRenewed.length}` : ''}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <TableContainer sx={{ maxHeight: 440 }}>
              <Table stickyHeader size="medium">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', background: '#f5f7fa' }}>Common Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', background: '#f5f7fa' }}>SAN Entry</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', background: '#f5f7fa', whiteSpace: 'nowrap' }}>AWS Account</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', background: '#f5f7fa' }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', background: '#f5f7fa', whiteSpace: 'nowrap' }}>Expiry Date</TableCell>
                    {renewalFilter === 'renewed' && (
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', background: '#f5f7fa', whiteSpace: 'nowrap' }}>Status</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingRenewals ? (
                    <TableRow>
                      <TableCell colSpan={renewalFilter === 'renewed' ? 6 : 5} align="center" sx={{ py: 3 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : renewalRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={renewalFilter === 'renewed' ? 6 : 5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                        {renewalFilter === 'renewed'
                          ? 'No certificates renewed in the last 30 days'
                          : 'No certificates due for renewal in the next 3 months'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    renewalRows.map((row, idx) => {
                      const sans = (row.subject_alt_names || '')
                        .split(',')
                        .map((s) => s.trim())
                        .filter(Boolean)
                      return (
                        <TableRow key={`${row.common_name}-${idx}`} hover>
                          <TableCell sx={{ fontSize: '0.875rem', wordBreak: 'break-all' }}>
                            {row.common_name}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem' }}>
                            {sans.length === 0 ? (
                              '—'
                            ) : (
                              <Tooltip title={sans.join(', ')} arrow>
                                <span>
                                  {sans[0]}
                                  {sans.length > 1 && (
                                    <Chip
                                      label={`+${sans.length - 1}`}
                                      size="small"
                                      sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }}
                                    />
                                  )}
                                </span>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                            {row.aws_account || '—'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem' }}>
                            {row.product || '—'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                            {formatDate(row.expiration_date)}
                          </TableCell>
                          {renewalFilter === 'renewed' && (
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <Chip
                                label={row.status || 'RENEWED'}
                                size="small"
                                color={row.status === 'FAILED' ? 'error' : 'success'}
                                variant="outlined"
                                sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
                              />
                            </TableCell>
                          )}
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Box>

      {/* Right Side - Login Form */}
      <Box
        sx={{
          width: { xs: '100%', md: '500px', lg: '600px' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: { xs: 3, sm: 6, md: 8 },
          background: 'white'
        }}
      >
        <Box sx={{ width: '100%', maxWidth: '400px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, justifyContent: 'center' }}>
            <Security sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#2f2f2f' }}>
                Sign In
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Use your Windows credentials
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Username"
              variant="outlined"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
              sx={{ mb: 2.5 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color="action" />
                  </InputAdornment>
                ),
              }}
              disabled={loading}
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              sx={{ mb: 4 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              disabled={loading}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                py: 1.8,
                background: 'linear-gradient(135deg, #004681 0%, #2f79bf 100%)',
                fontSize: '1.1rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 4px 15px rgba(0, 70, 129, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #003a6b 0%, #2667a8 100%)',
                  boxShadow: '0 6px 20px rgba(0, 70, 129, 0.6)',
                }
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
            </Button>
          </form>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Having trouble signing in? Contact IT Support
            </Typography>
          </Box>

          {/* Broadridge Logo */}
          <Box sx={{ mt: 6, mb: 3, textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
            <Box
              component="img"
              src="/images/broadridge-logo.png"
              alt="Broadridge Financial Solutions"
              sx={{
                height: 40,
                maxWidth: '200px',
                objectFit: 'contain',
                opacity: 0.8
              }}
              onError={(e) => {
                console.error('Logo failed to load')
                e.target.style.display = 'none'
              }}
            />
          </Box>

          {/* Copyright */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
              © {new Date().getFullYear()} Broadridge Financial Solutions, Inc.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default Login
